import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import Card from '../../../components/Card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import Loader from '../../../components/partials/components/loader';
import RenderDeleteModal from '../../../components/partials/components/delete-modal';
import { EditIcon, DeleteIcon } from '../../../components/setting/elements';
import { getApiCall, postApiCall } from '../../../service/httpCall';
import { API_AGENT_ANNOUNCEMENT_LIST, API_AGENT_ANNOUNCEMENT_DELETE } from '../../../service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '../../../utilities/constant';
import { useAuth, errorCheck } from '../auth/auth-utils';
import { getUser } from "../../../utilities/setting";
import exportData from '../../../utilities/export';

const AgentAnnouncementList = () => {
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const [dataList, setDataList] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [enableDeleteModal, setEnableDeleteModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isMultiDelete, setIsMultiDelete] = useState(false);
    const [isSuperAdmin, setIsSuperadmin] = useState(false);
    const [loggedInUserACL, setLoggedInUserACL] = useState({});
    const [buttonDisable, setButtonDisable] = useState(false);

    useEffect(() => {
        setLoading(true);
        const user = getUser();
            if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
            if (user?.acl_settings) setLoggedInUserACL(JSON.parse(user.acl_settings.module_operations));
        fetchData();
    }, []);

    const fetchData = () => {
        getApiCall(API_AGENT_ANNOUNCEMENT_LIST)
            .then((resp) => {
                const updated = resp.map((val, idx) => ({ ...val, key: idx + 1 }));
                setDataList(updated);
                setOriginalData(updated);
            })
            .catch((err) => {
                toast.error(JSON.stringify(err) || "Failed to fetch announcements");
                console.log("Error fetching announcements:", err);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (searchValue.trim()) {
            const filtered = originalData.filter(item =>
                item.heading?.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.content?.toLowerCase().includes(searchValue.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalData);
        }
    }, [searchValue, originalData]);

    useEffect(() => {
        if (confirmDelete) {
            const payload = {
                id: isMultiDelete ? selectedItems : deleteId
            };

            if (isMultiDelete && Array.isArray(selectedItems)) {
                const deletePromises = selectedItems.map(id =>
                    postApiCall(API_AGENT_ANNOUNCEMENT_DELETE, { id })
                );

                Promise.all(deletePromises)
                    .then(() => {
                        toast.success("Selected announcements deleted successfully");
                        fetchData();
                        setSelectedItems([]);
                    })
                    .catch((err) => {
                        toast.error(JSON.stringify(err) || "Failed to delete selected announcements");
                        console.log("Error deleting announcements:", err);
                    })
                    .finally(() => {
                        setConfirmDelete(false);
                        setEnableDeleteModal(false);
                    });
            } else {
                postApiCall(API_AGENT_ANNOUNCEMENT_DELETE, payload)
                    .then(() => {
                        toast.success("Announcement deleted successfully");
                        fetchData();
                        setSelectedItems([]);
                    })
                    .catch((err) => {
                        toast.error(JSON.stringify(err) || "Failed to delete announcement");
                        console.log("Error deleting announcement:", err);
                    })
                    .finally(() => {
                        setConfirmDelete(false);
                        setEnableDeleteModal(false);
                    });
            }
        }
    }, [confirmDelete]);

    const onError = (err) => errorCheck(err, navigate, () => { }, () => { });

    const handleSelectAll = (e) => {
        setSelectedItems(e.checked ? dataList.map(item => item.id) : []);
    };

    const actionTemplate = (rowData) => (
        <div>
            <div className="d-flex align-items-center list-user-action">
                {(isSuperAdmin || loggedInUserACL[MODULE_ACL.AGENT_ANNOUNCEMENT]?.edit) && (
                    // <EditIcon linkTo={`/${ROUTES.AGENT_ANNOUNCEMENT.EDIT.replace(':id', rowData.id)}`} />
                    <EditIcon linkTo={`/agent-announcements/edit/${rowData.id}`} />
                )}
                <div style={{ marginLeft: "10px" }}>
                {(isSuperAdmin || loggedInUserACL[MODULE_ACL.AGENT_ANNOUNCEMENT]?.delete) && selectedItems.length === 0 && (
                    <DeleteIcon handleCallback={() => {
                        setDeleteId(rowData.id);
                        setIsMultiDelete(false);
                        setEnableDeleteModal(true);
                    }} />
                )}
                </div>
            </div>
        </div>
    );

    if (loading) return <Loader />;

    return (
        <>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <h4 className="card-title">Agent Announcement List</h4>
                            {(isSuperAdmin || loggedInUserACL[MODULE_ACL.AGENT_ANNOUNCEMENT]?.add) && (
                                <Button onClick={() => navigate(`/${ROUTES.AGENT_ANNOUNCEMENT.CREATE}`)}>Create Announcement</Button>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex mb-3">
                                <Button variant="danger" className="me-2" disabled={selectedItems.length === 0} onClick={() => { setIsMultiDelete(true); setEnableDeleteModal(true); }}>Delete Selected</Button>
                                <Form.Control
                                    className="w-25"
                                    placeholder="Search announcements..."
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                            <DataTable value={dataList} paginator rows={10}
                                rowClassName={(data) => {
                                    const isDuplicate = dataList.filter(item =>
                                        item.heading?.toLowerCase().trim() === data.heading?.toLowerCase().trim()
                                    ).length > 1;

                                    return isDuplicate ? { 'bg-warning-subtle': true } : {};
                                }} style={{ width: "100%", overflowX: "scroll" }}>
                                <Column header={<Checkbox checked={selectedItems.length === dataList.length && dataList.length > 0} onChange={handleSelectAll} />}
                                    body={(rd) => <Checkbox checked={selectedItems.includes(rd.id)} onChange={() => setSelectedItems(prev => prev.includes(rd.id) ? prev.filter(i => i !== rd.id) : [...prev, rd.id])} />} />
                                <Column field="heading" header="Heading" />
                                <Column field="content" header="Content" style={{ maxWidth: "350px", width: "350px", overflowX: "scroll" }}/>
                                <Column field="created_at" header="Created Date" body={(rowData) =>
                                    new Date(rowData.created_at).toLocaleString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                    })
                                }></Column>
                                <Column field="updated_at" header="Updated Date" body={(rowData) =>
                                    new Date(rowData.updated_at).toLocaleString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                    })
                                }></Column>
                                <Column header="Action" body={actionTemplate} />
                            </DataTable>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <RenderDeleteModal show={enableDeleteModal} setShow={setEnableDeleteModal} setConfirmDelete={setConfirmDelete} isMultiDelete={isMultiDelete} />
        </>
    );
};

export default AgentAnnouncementList;